import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useAccountInfo } from '@app/context/AccountInfoContext/AccountInfoContext.Provider'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountGroupType } from '@model/AccountGroupType'
import { CreateDemoTradingAccount } from '@model/CreateTradingAccount'
import {
  PlatformTypeEnum,
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import { WalletCurrency } from '@model/WalletDto'

import { scrollToTop, useScrollToTop } from '@hooks/useScrollToTop'

import { CreateDemoTradingAccountForm } from './CreateDemoTradingAccountForm'

const useCreateTradingAccountCampaignMutate = (
  onSuccess: (values: CreateDemoTradingAccount) => Promise<void> | void
) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { refreshLimits } = useAccountInfo()
  const entity = useSessionEntity()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { addSnackbar } = useSnackbar()

  const [currencies, setCurrencies] = useState<WalletCurrency[]>([])

  const handleSubmit = async (values: CreateDemoTradingAccount) => {
    try {
      await apiClient.addDemoTradingAccount(values)

      queryClient.removeQueries({ queryKey: ['tradingAccount'] })

      scrollToTop()
      await onSuccess(values)
    } catch (error: unknown) {
      navigate('/dashboard/traders-room/trading-accounts')

      const message =
        (error as any)?.response?.response?.data?.message ??
        t('errors.Something went wrong! Please try again later')

      addSnackbar.error({ message: message })
    } finally {
      await refreshLimits()
    }
  }

  const handleChangePlatform = async (platformTypeId: number) => {
    const response = await apiClient.getDemoCurrencies({
      platformTypeId,
      tickmillCompanyId: entity,
    })
    setCurrencies(response)
  }

  return {
    currencies,
    handleSubmit,
    handleChangePlatform,
  }
}

const useCreateDemoTradingAccountFetch = () => {
  const apiClient = useApiClient(ClientApiClient)

  const [hasTMTOrTVAccount, setHasTMTOrTVAccount] = useState(false)

  useEffect(() => {
    const fetchAccounts = async () => {
      const tradingAccounts = await apiClient.getTradingAccounts({
        search: {
          AccountGroupType: {
            value: AccountGroupType.Live,
            operator: Operator.EQUAL,
          },
          Platform: {
            value: PlatformTypeEnum.CQGCAST,
            operator: Operator.NOT_EQUAL,
          },
        },
      })

      const tmtOrTVExists = tradingAccounts.items.some(
        (a) =>
          a.platformType.id === PlatformTypeEnum.TickmillTrader ||
          a.platformType.id === PlatformTypeEnum.TradingView
      )
      setHasTMTOrTVAccount(tmtOrTVExists)
    }

    fetchAccounts()
  }, [apiClient])

  return { hasTMTOrTVAccount }
}

export const CreateDemoTradingAccountPage: React.FC = () => {
  useScrollToTop()

  const location = useLocation()
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const entity = useSessionEntity()

  const { selectedPlatformType } = location.state || {}

  const handleCreateSuccess = useCallback(
    async (values: CreateDemoTradingAccount) => {
      const platformId = values.platformTypeId

      if (
        isTradingViewPlatformType(platformId) ||
        isMT4PlatformType(platformId) ||
        isMT5PlatformType(platformId) ||
        isTMTPlatformType(platformId)
      ) {
        navigate('success', { state: { canAccessSuccessPage: true } })
      } else {
        navigate('/dashboard')
      }
    },
    [navigate]
  )

  const { hasTMTOrTVAccount } = useCreateDemoTradingAccountFetch()
  const { currencies, handleSubmit, handleChangePlatform } =
    useCreateTradingAccountCampaignMutate(handleCreateSuccess)

  return (
    <CreateDemoTradingAccountForm
      entity={entity}
      currencies={currencies}
      account={account}
      selectedPlatformType={selectedPlatformType}
      onChangePlatform={handleChangePlatform}
      onSubmit={handleSubmit}
      hasTMTOrTVAccount={hasTMTOrTVAccount}
    />
  )
}
